import React, { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Image from "./image";
import styled from "styled-components";
import { modifyColor } from "../utils/helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const ScrollWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  overflow-x: hidden;
  scroll-snap-type: x mandatory;
`;

const Wrapper = styled.div`
  display: flex;
  overflow-x: hidden;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
`;

const StyledImage = styled(Image)`
  width: 5rem;
  height: 5rem;
  cursor: pointer;
  background-color: transparent;
  border-radius: var(--border-radius);
  filter: ${({ $active }) =>
    $active ? "brightness(100%)" : "brightness(95%)"};
  transition: filter 0.3s;
`;

const ImageContainer = styled.div`
  scroll-snap-align: start;
  flex-shrink: 0;
`;

const SlideButtonIcon = styled(FontAwesomeIcon)`
  background-color: var(--color-primary);
  color: var(--color-white);
  padding: 0.5rem;
  border-radius: var(--border-radius);
  width: 1em;
`;

const StyledButton = styled.button`
  background: transparent;
  padding: 0;
  margin-right: 1.25rem;
  margin-top: 1.25rem;
  margin-bottom: 1.25rem;
  border: ${({ $active }) =>
    $active ? "1px solid #ccc" : "1px solid transparent"};
  filter: drop-shadow(0px 0px 1.25rem rgba(0, 0, 0, 0.06));
  box-shadow: 0px 0px 1.25rem 0px rgba(0, 0, 0, 0.06);
  border-radius: var(--border-radius);
  transition: all 0.2s ease-in;
  @media (hover: hover) {
    &:hover {
      background-color: ${modifyColor(`var(--color-primary)`, 220)};
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.wideTablet}) {
    filter: none;
  }
`;

const CarouselButton = styled.button`
  background-color: transparent;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 1;
`;

export const PaginationImageDot = ({ index, onClick, image, active }) => {
  return (
    <StyledButton type="button" $active={active} onClick={() => onClick(index)}>
      <StyledImage $active={active} image={image} objectFit="contain" />
    </StyledButton>
  );
};

const ImagePagination = ({ images, index, onChangeIndex }) => {
  const scrollRef = useRef(null);
  const [showButtons, setShowButtons] = useState(false);

  useEffect(() => {
    if (scrollRef.current) {
      setShowButtons(
        scrollRef.current.scrollWidth > scrollRef.current.clientWidth
      );
    }
  }, [images]);

  const handlePrevClick = () => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.offsetWidth * 0.8;
      scrollRef.current.scrollLeft -= scrollAmount;
    }
  };

  const handleNextClick = () => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.offsetWidth * 0.8;
      scrollRef.current.scrollLeft += scrollAmount;
    }
  };

  return (
    <ScrollWrapper>
      {showButtons && (
        <CarouselButton onClick={handlePrevClick} style={{ left: 0 }}>
          <SlideButtonIcon icon={faChevronLeft} size="sm" />
        </CarouselButton>
      )}
      <Wrapper ref={scrollRef}>
        {images.map(
          (image, i) =>
            image?.localFile && (
              <ImageContainer key={i}>
                <PaginationImageDot
                  index={i}
                  active={i === index}
                  onClick={() => onChangeIndex(i)}
                  image={image}
                />
              </ImageContainer>
            )
        )}
      </Wrapper>
      {showButtons && (
        <CarouselButton onClick={handleNextClick} style={{ right: 0 }}>
          <SlideButtonIcon icon={faChevronRight} size="sm" />
        </CarouselButton>
      )}
    </ScrollWrapper>
  );
};

ImagePagination.propTypes = {
  images: PropTypes.arrayOf(PropTypes.object),
  index: PropTypes.number,
  onChangeIndex: PropTypes.func,
};

PaginationImageDot.propTypes = {
  index: PropTypes.number,
  onClick: PropTypes.func,
  image: PropTypes.object,
  active: PropTypes.bool,
};

export default ImagePagination;
